// Nomad-mon Go! — P7 game shell: title screen, screen state machine, deck
// builder, victory/defeat interstitials, settings drawer. Reading-size text is
// DOM-only (hard rule); the title keyart + dust live in the 3D scene (main.js
// owns them via the hooks passed here). Transitions share ONE veil fade so
// every screen change feels like the same hand turning the page.
//
// Screens: title | fan (collection showcase) | deck | battle | pack | inter
// Deck: 5 characters + 2 support cards, persisted to localStorage (SC32).

import * as audio from './audio.js';
import { artSrc } from './types.js';
import { openGuide } from './guide.js';
import * as commentary from './commentary.js';
import * as haptics from './haptics.js';
import { draftOpponent } from './scaling.js';
import { LEVEL_MILESTONES, levelProgress, milestoneAtLevel, HOLO_LEVEL, holoBorn } from './levelling.js';
import {
  detectSynergies, longestTypeArc, TYPE_ORDER as SYN_TYPES,
  GAUNTLET_UNLOCK_CARDS as GAUNTLET_UNLOCK,
} from './depth.js';
import {
  DECK_PICK, DRAW_COUNT, deriveMultiplayerDrawSeed, resolveDraw,
} from './draw.js';

const DECK_KEY = 'nomadmon-deck-v1';
const DIFF_KEY = 'nomadmon-ai-difficulty';   // SC3 (d): easy | normal | wrath
const V = 'v188';

let ctx = null;          // hooks from main.js
let screen = 'title';
let deckSel = { chars: [], supports: [] };   // arrays of card ids
let ownedFn = null;      // SC2-B: (id) => owned? — player decks use OWNED cards only
let interOnContinue = null;
let interOnRematch = null;
let interOnShare = null;       // SC3: victory-brag share handler
let mpDeckHandler = null;      // set while a multiplayer match is picking decks
let mpDrawOpts = null;         // room seed + player side for reproducible MP draws
export function setMpDeckMode(fn, opts = null) { mpDeckHandler = fn; mpDrawOpts = fn ? opts : null; }

const $ = (id) => document.getElementById(id);

// P8 Move 4: thumbnails honor an optional per-card thumbFocal {x,y}
function thumbPos(d) {
  const tf = d.thumbFocal;
  return `${(((tf && tf.x) ?? d.focalX ?? 0.5) * 100).toFixed(1)}% ` +
    `${(((tf && tf.y) ?? 0.5) * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------- veil
let veilBusy = false;
async function veilTo(fn) {
  const veil = $('veil');
  if (veilBusy) { fn(); return; }            // never deadlock on double-tap
  veilBusy = true;
  veil.classList.add('on');
  await new Promise(r => setTimeout(r, 400));
  fn();
  await new Promise(r => setTimeout(r, 60)); // let the new screen paint
  veil.classList.remove('on');
  veilBusy = false;
}

function setBodyMode() {
  document.body.classList.toggle('on-title', screen === 'title');
  document.body.classList.toggle('on-shell',
    screen === 'deck' || screen === 'draw' || screen === 'inter' || screen === 'mp' || screen === 'lb');
}

function showScreenEl(name) {
  document.body.dataset.screen = name;     // SC9-A: nav visibility hooks
  $('screenTitle').classList.toggle('open', name === 'title');
  $('deckbuilder').classList.toggle('open', name === 'deck');
  const draw = $('slotDraw'); if (draw) draw.classList.toggle('open', name === 'draw');
  $('interstitial').classList.toggle('open', name === 'inter');
  $('mpScreen').classList.toggle('open', name === 'mp');
  $('lbScreen').classList.toggle('open', name === 'lb');
}

export function currentScreen() { return screen; }

// SC6: one greeting per page load — Shao's title call layered over the theme.
// Fired by the entry tap (main.js) or, failing that, the first title gesture.
let greeted = false;
// A returning-player line follows the title call on the same VO channel.
// Awaiting the source's real `ended` event keeps the two lines serial without
// guessing their durations or allowing them to overlap.
export function greet(delay = 350, altLine = null) {
  if (greeted) return;
  greeted = true;
  audio.voPreload(['title-call', 'victory', 'defeat', ...(altLine ? [altLine] : [])]);
  setTimeout(async () => {
    await audio.vo('title-call', { interrupt: true });
    if (altLine) await audio.vo(altLine);
  }, delay);
  audio.playPool('title');
}

// go(name) — the ONE transition path
export function go(name, opts = {}) {
  const from = screen;
  if (name === from && name !== 'inter') return;
  screen = name;
  return veilTo(() => {
    // SC2-B fix: a queued veil fn from a superseded go() must not reassert a
    // stale screen (repro: showcase -> title -> Open Pack inside the 400ms
    // veil left the title menu painted over the pack cinematic)
    if (screen !== name) return;
    showScreenEl(name);
    setBodyMode();
    if (name === 'title') {
      ctx.showTitle3D();
      audio.playPool('title');                 // P4: manifest title pool, LOUD
    } else {
      // The target owns transition visibility. Only COLLECTION (`fan`) may
      // reveal the shared collection renderer during the handoff.
      ctx.hideTitle3D(name === 'fan');
    }
    // SC3 (d): MP deck picks hide the solo difficulty row
    if (name === 'deck') $('deckbuilder').classList.toggle('mpdeck', !!opts.mpPending);
    // SC2-A deck carousel owns the frame while the deck screen is up
    if (name === 'deck' && ctx.showDeck3D) ctx.showDeck3D();
    else if (from === 'deck' && ctx.hideDeck3D) ctx.hideDeck3D();
    if (name === 'fan') {
      audio.playPool('title');                 // fan/binder keeps the menu pool
      if (ctx.prepFan) ctx.prepFan();          // SC9-B: MY DECK composition
      setTimeout(() => audio.screenVo('mydeck'), 500);   // SC9-E
    }
    if (name === 'deck') setTimeout(() => audio.screenVo('deckpick'), 500);
    if (name === 'lb') {
      renderLeaderboard();                     // SC3 (e)
      setTimeout(() => audio.screenVo('leaderboard'), 500);
    }
    if (opts.after) opts.after();
  });
}

// ---------------------------------------------------------------- deck logic
// SC32: decks are FIVE characters + two supports, and a battle is won by
// knocking out three of the opponent's five. Five slots give real bench depth
// and genuine swap decisions; first-to-three keeps the battle from doubling in
// length (sim: 12.7 average rounds, not 25).
export const DECK_CHARS = 5, DECK_SUPPORTS = 2;
export function isSupport(d) { return d.role === 'support'; }

export function loadDeck(deck) {
  let sel = null;
  try { sel = JSON.parse(localStorage.getItem(DECK_KEY)); } catch { /* fresh */ }
  const ids = new Set(deck.map(d => d.id));
  // SC2-B: stored selections drop cards the player doesn't own (guest data is
  // pre-seeded by identity's deck migration, so guest decks survive intact)
  const legal = (id) => ids.has(id) && (!ownedFn || ownedFn(id));
  deckSel = {
    chars: (sel?.chars || []).filter(legal).slice(0, DECK_PICK),
    supports: (sel?.supports || []).filter(legal).slice(0, DECK_SUPPORTS),
  };
  // deck integrity: chars must be characters, supports must be supports
  const byId = new Map(deck.map(d => [d.id, d]));
  deckSel.chars = deckSel.chars.filter(id => !isSupport(byId.get(id)));
  deckSel.supports = deckSel.supports.filter(id => isSupport(byId.get(id)));
  // COMBAT V3 softlock fix: a deck with its fighters picked but too few owned
  // supports used to leave START disabled forever. Top it up from what the
  // player owns (the starter pair is always owned — identity.STARTER_SUPPORTS).
  if (deckSel.supports.length < DECK_SUPPORTS) {
    for (const d of deck) {
      if (deckSel.supports.length >= DECK_SUPPORTS) break;
      if (isSupport(d) && legal(d.id) && !deckSel.supports.includes(d.id)) deckSel.supports.push(d.id);
    }
  }
  return deckSel;
}
function saveDeck() {
  try { localStorage.setItem(DECK_KEY, JSON.stringify(deckSel)); } catch { /* private */ }
  if (ctx?.syncWhatsAppDeck) void ctx.syncWhatsAppDeck(deckSel);
}
export function difficulty() {
  try { const d = localStorage.getItem(DIFF_KEY); return ['easy', 'normal', 'wrath'].includes(d) ? d : 'normal'; }
  catch { return 'normal'; }
}
export function deckLegal() {
  return deckSel.chars.length === DECK_PICK && deckSel.supports.length === DECK_SUPPORTS;
}
export function selectedDeck() { return deckSel; }

export function quickDeck(deck, rng = Math.random) {
  // legal random deck with a bias toward higher rarity + type spread
  // SC2-B: fills from OWNED cards only (aiDeck stays unrestricted)
  const RW = { eternal: 6, mythic: 5, legendary: 4, epic: 3, rare: 2, common: 1 };
  const pool = ownedFn ? deck.filter(d => ownedFn(d.id)) : deck;
  const chars = pool.filter(d => !isSupport(d));
  const sups = pool.filter(isSupport);
  const pickWeighted = (pool, n, diversify) => {
    const out = [];
    const used = new Set();
    const usedTypes = new Set();
    let guard = 400;
    while (out.length < n && guard-- > 0) {
      const total = pool.reduce((a, d) => a + (used.has(d.id) ? 0 : RW[d.rarity] || 1), 0);
      let r = rng() * total;
      for (const d of pool) {
        if (used.has(d.id)) continue;
        r -= RW[d.rarity] || 1;
        if (r <= 0) {
          if (diversify && usedTypes.has(d.type) && rng() < 0.6 && guard > 60) break;
          used.add(d.id); usedTypes.add(d.type); out.push(d.id);
          break;
        }
      }
    }
    return out;
  };
  deckSel = {
    chars: pickWeighted(chars, DECK_PICK, true),
    supports: pickWeighted(sups, DECK_SUPPORTS, false),
  };
  saveDeck();
  return deckSel;
}

// ---------------------------------------------------------------- win streak
// Consecutive solo wins, used to widen the opponent bracket. Local and cheap:
// the server re-derives it for named players when it pays out rewards, so this
// copy only ever drives presentation and the (client-side) opponent draft.
const STREAK_KEY = 'nomadmon-win-streak';
const SOLO_WINS_KEY = 'nomadmon-solo-wins-v1';
export function winStreak() {
  try { return Math.max(0, parseInt(localStorage.getItem(STREAK_KEY), 10) || 0); }
  catch { return 0; }
}
export function setWinStreak(n) {
  try { localStorage.setItem(STREAK_KEY, String(Math.max(0, n | 0))); } catch { /* private */ }
}
export function recordStreak(won) {
  const next = won ? winStreak() + 1 : 0;
  setWinStreak(next);
  return next;
}

// Lifetime SOLO wins drive only the invisible five-win onboarding ramp. Seed a
// missing v68-era key from the existing W-L record so veterans do not get
// mistaken for first-time players after upgrading. Losses deliberately leave
// this counter alone: the ramp is earned away through wins, not attempts.
function soloWinsKey(playerKey = 'guest') {
  return `${SOLO_WINS_KEY}:${String(playerKey || 'guest').trim().toLowerCase()}`;
}
export function soloWins(existingWins = 0, playerKey = 'guest') {
  try {
    const key = soloWinsKey(playerKey);
    const stored = localStorage.getItem(key);
    if (stored != null) return Math.max(0, parseInt(stored, 10) || 0);
    const seeded = Math.max(0, existingWins | 0);
    localStorage.setItem(key, String(seeded));
    return seeded;
  } catch { return Math.max(0, existingWins | 0); }
}
export function recordSoloWin(won, existingWins = 0, playerKey = 'guest') {
  const current = soloWins(existingWins, playerKey);
  const next = won ? current + 1 : current;
  try { localStorage.setItem(soloWinsKey(playerKey), String(next)); } catch { /* private */ }
  return next;
}

// AI opponent deck. SC32: no longer a uniform random roll. The sim measured
// that opponent DECK POWER is worth ~84 points of win-rate while AI search
// depth is worth ~4 — a clever AI holding a weak hand is still a punching bag,
// which is exactly why the old build never stopped being easy as the player's
// collection grew. The opponent is now drafted to MATCH the player rank for
// rank, biased by difficulty and widened by the win streak (src/scaling.js).
export function aiDeck(deck, rng = Math.random, opts = {}) {
  const byId = Object.fromEntries(deck.map(d => [d.id, d]));
  // v11 FORGED: Duos/Fusions only ever enter an AI draft where they belong —
  // WRATH, the GAUNTLET legs, or as an answer to a player who fields one
  // (rank-matched, so they are picked only when the target power calls for
  // them). EASY/NORMAL against an unforged squad never meet one.
  const playerIds = opts.playerChars || deckSel.chars;
  const allowForged = (opts.difficulty || difficulty()) === 'wrath' || opts.biasOverride != null
    || playerIds.some(id => byId[id] && byId[id].forged);
  const chars = deck.filter(d => !isSupport(d) && (allowForged || !d.forged));
  const sups = deck.filter(isSupport);
  if (!deckSel.chars.length) {
    // no player deck yet (shouldn't happen from the battle path) — fall back to
    // a plain shuffle rather than dividing by zero.
    const sh = (a) => a.map(x => [rng(), x]).sort((p, q) => p[0] - q[0]).map(p => p[1]);
    return {
      chars: sh(chars.filter(d => !d.forged)).slice(0, DECK_CHARS).map(d => d.id),
      supports: sh(sups).slice(0, DECK_SUPPORTS).map(d => d.id),
    };
  }
  const seed = opts.seed != null ? opts.seed : Math.floor(rng() * 0x7fffffff);
  return draftOpponent({
    // LUCKY DRAW: deckSel.chars is the two PICKS; the battle passes the resolved five
    playerChars: opts.playerChars || deckSel.chars, pool: chars, sups, byId,
    playerLevels: opts.playerLevels || {},
    streak: opts.streak != null ? opts.streak : winStreak(),
    newPlayerWins: opts.newPlayerWins != null ? opts.newPlayerWins
      : soloWins(opts.existingWins || 0, opts.playerKey || 'guest'),
    difficulty: opts.difficulty || difficulty(),
    seed, nChars: DECK_CHARS, nSupports: DECK_SUPPORTS,
    // SC33 gauntlet: the legs are drafted at an explicit bias, not the tier's.
    biasOverride: opts.biasOverride != null ? opts.biasOverride : null,
  });
}

// ---------------------------------------------------------------- deck builder UI
function paintSlots(deck) {
  const byId = new Map(deck.map(d => [d.id, d]));
  const slotFill = (slotName, id) => {
    const el = document.querySelector(`.dslot[data-slot="${slotName}"]`);
    const img = el.querySelector('img');
    if (id) {
      const d = byId.get(id);
      el.classList.add('filled');
      img.src = artSrc(d, V);
      img.style.objectPosition = thumbPos(d);
    } else {
      el.classList.remove('filled');
      img.removeAttribute('src');
    }
  };
  for (let i = 0; i < DECK_PICK; i++) slotFill('c' + i, deckSel.chars[i]);
  for (let i = 0; i < DECK_SUPPORTS; i++) slotFill('s' + i, deckSel.supports[i]);
  $('deckCount').textContent =
    `${deckSel.chars.length}/${DECK_PICK} picked · ${DRAW_COUNT} drawn · ${deckSel.supports.length}/${DECK_SUPPORTS} support`;
  const oc = ownedFn ? deck.filter(d => !isSupport(d) && ownedFn(d.id)).length : DECK_CHARS;
  const os = ownedFn ? deck.filter(d => isSupport(d) && ownedFn(d.id)).length : DECK_SUPPORTS;
  const ready = deckLegal() && oc >= DECK_CHARS && os >= DECK_SUPPORTS;
  const wasLegal = !$('deckStart').disabled;
  $('deckStart').disabled = !ready;
  if (ready && !wasLegal) audio.vo('deck-ready');
  // SC2-B: a fresh account that can't field a legal deck gets pointed at packs
  // (START stays disabled via deckLegal above)
  if (ownedFn) {
    $('deckHint').textContent = (oc < DECK_CHARS || os < DECK_SUPPORTS)
      ? 'Open packs to recruit more Nomads' : deckHintDefault;
  }
  paintDeckSynergies(byId);
  if (onDeckChange) onDeckChange();
}
let deckHintDefault = '';
let gauntletMode = false;
let forgeLaunch = null, forgeLucky = null;   // V2 FORGE: set by initShell      // SC33: the deck picker is arming a gauntlet run

// ---------------------------------------------------------------- SC33 synergy
// The deck picker's live read-out. This is the point of the whole synergy
// system: while you are BUILDING, you can see what the five you have chosen
// would activate — and, just as importantly, what they ALMOST activate. A
// "near" badge (dimmed, one card short) is what turns a 1,558-card collection
// from a pile into a puzzle.
function paintDeckSynergies(byId) {
  const el = $('deckSyn');
  if (!el) return;
  try {
    const ids = deckSel.chars.filter(Boolean);
    const lookup = {};
    for (const id of ids) { const d = byId.get ? byId.get(id) : byId[id]; if (d) lookup[id] = d; }
    const { list } = detectSynergies(ids, lookup);
    const active = new Set(list.map(x => x.key));
    // ---- SC38d: A BADGE THAT NAMES ITSELF IN JARGON TEACHES NOTHING -------
    // Jack, on live v45: "I didn't even know what TYPE ARC means." That is a
    // legibility complaint, not a layout one, so the fix is not to relocate the
    // chip — it is to stop printing the codename. The badge now leads with the
    // EFFECT, which is the only part a player can act on, and the codename
    // survives only in the tooltip and the Card Guide. Same pixels, and it
    // answers the question it used to raise.
    const effectOf = (b) => {
      const bits = [];
      if (b && b.energyPerRound) bits.push(`+${b.energyPerRound} ENERGY/RD`);
      if (b && b.dmgPct) bits.push(`+${b.dmgPct}% DMG`);
      if (b && b.chargeFaster) bits.push(`CHARGE −${b.chargeFaster}`);
      return bits.join(' · ');
    };
    const out = list.map(x => {
      const cls = x.key === 'underdog' ? ' under'
        : (x.key === 'labborn' || x.key === 'labborn3') ? ' lab' : '';
      const label = effectOf(x.bonus) || x.label;
      return `<span class="synbadge${cls}" title="${x.label} — ${x.blurb}">` +
        `<i aria-hidden="true">&#9670;</i>${label}</span>`;
    });
    // ---- ONE CARD SHORT: the hint that makes the next pick interesting ----
    if (ids.length >= 2 && ids.length < 5) {
      const cards = ids.map(id => lookup[id]).filter(Boolean);
      const near = [];
      if (!active.has('arc3') && !active.has('arc4')) {
        const arc = longestTypeArc(cards.map(c => c.type));
        // plain language for the near-miss too: what to DO and what it pays
        if (arc.len === 2) near.push('1 MORE NEIGHBOURING TYPE &rarr; +1 ENERGY/RD');
      }
      if (!active.has('person')) {
        const byP = {};
        for (const c of cards) if (c.person) byP[c.person] = (byP[c.person] || 0) + 1;
        const p = Object.entries(byP).find(([, n]) => n === 2);
        if (p) near.push(`1 MORE ${p[0].toUpperCase()} &rarr; BONUS`);
      }
      for (const n of near.slice(0, 2)) {
        out.push(`<span class="synbadge near" title="Not active yet">` +
          `<i aria-hidden="true">&#9671;</i>${n}</span>`);
      }
    }
    el.innerHTML = out.join('');
  } catch { el.innerHTML = ''; }
}

// SC2-A: the carousel (src/deckpicker.js) is the ONE picking surface; the
// shell keeps deck state + tray chrome. onDeckChange lets the carousel react.
let onDeckChange = null;
export function setOnDeckChange(fn) { onDeckChange = fn; }

function toggleCard(deck, id) {
  const d = deck.find(x => x.id === id);
  const pool = isSupport(d) ? deckSel.supports : deckSel.chars;
  const cap = isSupport(d) ? DECK_SUPPORTS : DECK_PICK;
  const at = pool.indexOf(id);
  if (at >= 0) pool.splice(at, 1);
  else if (pool.length < cap) pool.push(id);
  else return false;                             // full
  saveDeck();
  paintSlots(deck);
  return true;
}

// ---------------------------------------------------------------- interstitial
// result: { won, stats: { damage, rounds, kos, mvp: {id, name, art, focalX, damage}, drawMvp }, line }
// R3: one live burst of PRISM confetti behind the victory results (theme-v2.css
// #interConfetti). Pieces are placed by index hash, not Math.random — the same
// burst every time; transform/opacity keyframes only; `.on` comes off when the
// last piece has landed so the layer is visibility:hidden again.
const CONF_COLS = ['var(--v2-cyan)', 'var(--v2-blue)', 'var(--v2-violet)', 'var(--v2-magenta)', 'var(--v2-amber)', 'var(--v2-lime)'];
let confT = 0;
function confettiBurst(inter, on) {
  let host = $('interConfetti');
  if (!host) {
    host = document.createElement('div');
    host.id = 'interConfetti';
    host.setAttribute('aria-hidden', 'true');
    const h = (i, s) => { const x = Math.sin((i + 1) * 12.9898 + s * 78.233) * 43758.5453; return x - Math.floor(x); };
    let html = '';
    for (let i = 0; i < 44; i++) {
      html += `<i style="left:${(h(i, 1) * 100).toFixed(1)}%;--cc:${CONF_COLS[i % CONF_COLS.length]};` +
        `--cd:${(2.0 + h(i, 2) * 1.2).toFixed(2)}s;--cw:${(h(i, 3) * 0.7).toFixed(2)}s;` +
        `--cx:${((h(i, 4) - 0.5) * 160).toFixed(0)}px;--cr:${(360 + h(i, 5) * 720).toFixed(0)}deg;` +
        `width:${(6 + h(i, 6) * 6).toFixed(1)}px"></i>`;
    }
    host.innerHTML = html;
    inter.appendChild(host);
  }
  clearTimeout(confT);
  host.classList.remove('on');
  if (!on) return;
  void host.offsetWidth;                   // restart the burst on a rematch win
  host.classList.add('on');
  confT = setTimeout(() => host.classList.remove('on'), 4200);
}

export function showInterstitial(result, { onContinue, onRematch, share } = {}) {
  const inter = $('interstitial');
  inter.classList.toggle('defeat', !result.won);
  $('interBg').src = `./assets/bg/${result.won ? 'victory' : 'defeat'}.webp?${V}`;
  $('interWord').textContent = result.won ? 'Victory' : 'Defeat';
  $('interNarr').textContent = result.line || '';
  const s = result.stats || {};
  // Exactly ONE card is the MVP: the top damage dealer. The best of the three
  // cards fate dealt gets its own honest label — and none at all when it is the
  // MVP itself (then the MVP tile says so) or never landed a hit. v127 printed
  // "MVP OF THE DRAW · 0 DMG" beside a second "MVP".
  const mvp = s.mvp && !(s.mvp.damage <= 0) ? s.mvp : null;
  const drawIsMvp = !!(mvp && s.drawMvp && (s.drawMvp.id ?? s.drawMvp.name) === (mvp.id ?? mvp.name));
  const drawMvp = s.drawMvp && !drawIsMvp && s.drawMvp.damage > 0 ? s.drawMvp : null;
  const tile = (c, cls, label) => `<div class="istat ${cls}"><img src="${artSrc(c, V)}" alt="" ` +
    `style="object-position:${((c.focalX ?? 0.5) * 100).toFixed(1)}% 50%"><div class="v">${c.name}</div>` +
    `<div class="k">${label}</div></div>`;
  const dmg = (c) => (c.damage != null ? ` · ${c.damage} DMG` : '');
  $('interStats').innerHTML = `
    ${mvp ? tile(mvp, 'mvp', `MVP${drawIsMvp ? ' · dealt by fate' : ''}${dmg(mvp)}`) : ''}
    ${drawMvp ? tile(drawMvp, 'drawmvp', `Best of the draw${dmg(drawMvp)}`) : ''}
    <div class="istat"><div class="v">${s.damage ?? 0}</div><div class="k">Damage Dealt</div></div>
    <div class="istat"><div class="v">${s.rounds ?? 0}</div><div class="k">Rounds</div></div>
    ${s.kos != null ? `<div class="istat"><div class="v">${s.kos}</div><div class="k">Knockouts</div></div>` : ''}`;
  const progress = $('interProgress');
  if (progress) { progress.innerHTML = ''; progress.classList.remove('show'); }
  interOnContinue = onContinue || (() => go('title'));
  interOnRematch = onRematch || null;
  interOnShare = share || null;                       // SC3 (c): victory brag
  $('interShare').style.display = interOnShare ? '' : 'none';
  $('interRematch').style.display = interOnRematch ? '' : 'none';
  screen = 'inter';
  showScreenEl('inter');
  setBodyMode();
  audio.vo(result.won ? 'victory' : 'defeat', { interrupt: true });
  if (result.won) { audio.bplay('fanfare', { gain: 0.8 }); haptics.buzz(haptics.PATTERNS.victory); }
  confettiBurst(inter, !!result.won);
  // the reward lands a beat later (the server has to pay it first)
  const rw = $('interReward');
  if (rw) { rw.innerHTML = ''; rw.classList.remove('show'); }
}

export function showBattleProgress(rows, cardsById) {
  const root = $('interProgress');
  if (!root || screen !== 'inter' || !Array.isArray(rows) || !rows.length) return;
  const body = rows.map((row) => {
    const d = cardsById[row.id];
    if (!d) return '';
    const after = levelProgress(row.xp);
    const before = levelProgress(Math.max(0, row.xp - (row.gained || 0)));
    const crossed = row.level > (row.beforeLevel || row.level);
    const hit = LEVEL_MILESTONES.find(m =>
      m.level > (row.beforeLevel || row.level) && m.level <= row.level);
    const hitNow = crossed && !!hit;
    const nextLabel = milestoneAtLevel(row.level + 1)?.level === row.level + 1
      ? ` · ${milestoneAtLevel(row.level + 1).label}` : '';
    const near = after.maxed ? 'MAX LEVEL'
      : after.remaining <= (row.gained || 0)
        ? `ONE MORE WIN → LVL ${row.level + 1}${nextLabel}`
        : `${after.remaining} XP TO LVL ${row.level + 1}${nextLabel}`;
    const start = crossed ? 0 : before.ratio;
    const finish = after.ratio;
    // v10 HOLO: crossing the Holo level is its own moment on the victory screen.
    const holoUp = crossed && (row.beforeLevel || row.level) < HOLO_LEVEL && row.level >= HOLO_LEVEL && !holoBorn(d);
    return `<div class="ixp-row${holoUp ? ' holo-up' : ''}" data-fill="${finish.toFixed(4)}">` +
      `<img src="${artSrc(d, V)}" alt="" style="object-position:${thumbPos(d)}">` +
      `<div class="ixp-name">${d.name}<span>LVL ${row.level}</span></div>` +
      `<div><div class="ixp-track"><div class="ixp-fill" style="transform:scaleX(${start.toFixed(4)})"></div></div>` +
      `<div class="ixp-near${after.remaining <= (row.gained || 0) && !after.maxed ? ' hot' : ''}">${near}</div></div>` +
      `<div class="ixp-gain">+${row.gained || 0} XP<span>${holoUp ? 'Holo unlocked' : hitNow ? hit.label : crossed ? 'Level up' : ''}</span></div></div>`;
  }).join('');
  root.innerHTML = `<div class="ixp-head"><span>Card XP</span><span>Victory trains all five</span></div>${body}`;
  root.classList.add('show');
  if (root.querySelector('.ixp-row.holo-up')) setTimeout(() => audio.play('shimmer', { gain: 0.6 }), 900);
  requestAnimationFrame(() => requestAnimationFrame(() => {
    for (const row of root.querySelectorAll('.ixp-row')) {
      const fill = row.querySelector('.ixp-fill');
      if (fill) fill.style.transform = `scaleX(${row.dataset.fill})`;
    }
  }));
}

// SC32: the post-battle reward, itemised. Called once the server (or, for a
// guest, the local mirror) has settled what the battle was worth. Fighting up a
// bracket and holding a streak has to be VISIBLE or it is not a reason to do it.
// ---------------------------------------------------------------- SC33 gauntlet
// Two panels: the between-legs briefing (which shows the squad you have LEFT
// and how hurt it is — the whole tension of the mode) and the end-of-run card.
let gauntOnContinue = null;
function gauntPanel(on) {
  const el = $('gauntPanel');
  if (!el) return null;
  el.classList.toggle('on', !!on);
  el.setAttribute('aria-hidden', on ? 'false' : 'true');
  return el;
}
function squadHTML(squad, cardsById) {
  return (squad || []).map(c => {
    const d = cardsById[c.id];
    if (!d) return '';
    const frac = c.hp / (d.hp || 1);
    const cls = frac <= 0.25 ? ' crit' : frac <= 0.55 ? ' low' : '';
    return `<div class="gq"><img src="${artSrc(d, V)}" alt="" ` +
      `style="object-position:${thumbPos(d)}">` +
      `<span class="gqhp${cls}">${c.hp}/${d.hp}</span></div>`;
  }).join('');
}
// The title button's state: locked (with the count still to go), unlocked, or
// unlocked and already cleared once.
export function paintGauntletButton() {
  const b = $('menuGauntlet');
  if (!b) return;
  const unlocked = !!(ctx && ctx.gauntletUnlocked && ctx.gauntletUnlocked());
  const cleared = !!(ctx && ctx.gauntletCleared && ctx.gauntletCleared());
  b.classList.toggle('locked', !unlocked);
  b.classList.toggle('cleared', unlocked && cleared);
  const sub = $('gauntSub');
  if (!sub) return;
  if (!unlocked) {
    const have = (ctx && ctx.ownedDistinct && ctx.ownedDistinct()) || 0;
    sub.textContent = `locked \u00b7 ${have}/${GAUNTLET_UNLOCK} cards owned`;
  } else {
    sub.textContent = cleared
      ? 'three battles \u00b7 no healing \u00b7 CLEARED'
      : 'three battles \u00b7 no healing';
  }
}

export function showGauntletLeg({ next, label, squad, cardsById, onContinue }) {
  const el = gauntPanel(true);
  if (!el) { if (onContinue) onContinue(); return; }
  $('gpK').textContent = `Leg ${next + 1} of 3`;
  $('gpT').textContent = label || '';
  $('gpB').textContent = 'No healing. These are the cards you have left.';
  $('gpSquad').innerHTML = squadHTML(squad, cardsById);
  $('gpRw').textContent = '';
  $('gpBtn').textContent = 'March on';
  gauntOnContinue = () => { gauntPanel(false); if (onContinue) onContinue(); };
  audio.play('shimmer', { gain: 0.6 });
}
export function showGauntletEnd({ cleared, legs, reward }) {
  const el = gauntPanel(true);
  if (!el) return;
  $('gpK').textContent = cleared ? 'Gauntlet cleared' : 'The run ends';
  $('gpT').textContent = cleared ? 'THE LAST NOMAD FALLS' : `You reached leg ${legs + 1}`;
  $('gpB').textContent = cleared
    ? 'Somebody had to be the one who watched the rest of it leave. It was not going to be you.'
    : 'The road takes what it takes. Bring a deeper bench.';
  $('gpSquad').innerHTML = '';
  $('gpRw').innerHTML = reward && reward.total
    ? `+${reward.total} pack credit${reward.total === 1 ? '' : 's'} · ` +
      (reward.parts || []).map(p => `${p.label} +${p.packs}`).join(' · ')
    : 'No credits earned.';
  $('gpBtn').textContent = 'Back to the title';
  gauntOnContinue = () => { gauntPanel(false); go('title'); };
  audio.play(cleared ? 'deckpick' : 'tick', { gain: 0.8 });
}

export function showBattleReward(reward) {
  const rw = $('interReward');
  if (!rw) return;
  if (!reward || !reward.packs) { rw.innerHTML = ''; rw.classList.remove('show'); return; }
  const parts = (reward.parts || []).map(p =>
    `<span class="rwpart">${p.label} <b>+${p.packs}</b></span>`).join('');
  rw.innerHTML =
    `<div class="rwtot"><span class="rwn">+${reward.packs}</span>` +
    `<span class="rwl">pack credit${reward.packs === 1 ? '' : 's'}</span></div>` +
    `<div class="rwparts">${parts}</div>`;
  rw.classList.add('show');
  audio.play('deckpick', { gain: 0.7 });
}


// ---------------------------------------------------------------- SC3 (e): leaderboard
// Server W-L records -> ranked table. Medals for the top three ranked players
// (at least one recorded game); your row is highlighted; claimed-but-unplayed
// players trail unranked.
async function renderLeaderboard() {
  const list = $('lbList');
  list.innerHTML = '<div class="pk-loading">Consulting the records\u2026</div>';
  let players = null;
  try { players = await ctx.fetchPlayers(); } catch { /* offline */ }
  list.innerHTML = '';
  const me = (ctx.playerName && ctx.playerName()) || null;
  const claimed = (players || []).filter(p => p.claimed);
  if (!claimed.length) {
    list.innerHTML = '<div class="lbEmpty">No champions yet \u2014 claim a name and fight</div>';
    return;
  }
  const games = (p) => (p.wins || 0) + (p.losses || 0);
  const winrate = (p) => (games(p) ? (p.wins || 0) / games(p) : 0);
  const ranked = claimed.filter(p => games(p) > 0)
    .sort((a, b) => (b.wins - a.wins) || (winrate(b) - winrate(a)) || (b.cards - a.cards));
  const idle = claimed.filter(p => games(p) === 0)
    .sort((a, b) => (b.cards || 0) - (a.cards || 0));
  const MEDALS = ['\u{1F947}', '\u{1F948}', '\u{1F949}'];
  const row = (p, i, isRanked) => {
    const div = document.createElement('div');
    div.className = 'lbRow' + (p.name === me ? ' me' : '') + (isRanked ? '' : ' unranked');
    const medal = isRanked && i < 3;
    div.innerHTML =
      `<span class="lbRank${medal ? ' medal' : ''}">${medal ? MEDALS[i] : (isRanked ? i + 1 : '\u2013')}</span>` +
      `<span class="lbName">${p.name}` +
        // SC33: the gauntlet mark is server-reported, so a client flag cannot
        // put a star on someone else's row.
        `${p.gauntletCleared ? '<i class="chipmark" title="Boss Gauntlet cleared">\u2605</i>' : ''}</span>` +
      `<span class="lbRec">${p.wins || 0}\u2013${p.losses || 0}</span>` +
      `<span class="lbMeta">${isRanked ? Math.round(winrate(p) * 100) + '% \u00b7 ' : ''}${p.cards || 0} cards</span>`;
    list.appendChild(div);
  };
  ranked.forEach((p, i) => row(p, i, true));
  idle.forEach((p, i) => row(p, i, false));
}

// ---------------------------------------------------------------- settings
function paintToggles() {
  $('setSound').classList.toggle('on', !audio.muted());
  $('setNarrator').classList.toggle('on', audio.narratorOn());
  $('setShao').classList.toggle('on', commentary.explicitOn());
  $('setHaptics').classList.toggle('on', haptics.supported() && haptics.enabled());
  $('setCine').classList.toggle('on', localStorage.getItem('nomadmon-cine') !== '0');
  const link = $('setWhatsApp');
  if (link) {
    const available = !!ctx.canUseWhatsAppChallenges?.();
    link.disabled = !available;
    link.textContent = available ? 'Get code' : 'Claim device';
  }
}

// SC3 (b): PWA install nudge — the browser hands us the prompt, we hold it
let installPrompt = null;
addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  installPrompt = e;
  const row = $('installRow');
  if (row) row.style.display = '';
});
/**
 * SC36. Report the live graphics tier in the drawer. The shed is a RUNTIME
 * decision that can flip mid-session (SC34), so this is painted every time the
 * drawer opens rather than once at boot — a stale "Full" would be worse than no
 * readout at all, because it would answer the question wrongly.
 */
function paintQuality() {
  const el = $('setQuality');
  if (!el || !ctx.quality) return;
  let q; try { q = ctx.quality(); } catch { return; }
  const actual = Number(q.dpr).toFixed(1), native = Number(q.nativeDpr ?? q.devDpr).toFixed(1);
  el.textContent = q.reduced
    ? `DROPPED QUALITY ${actual}× of ${native}× · bloom off · ${q.sheds} drops`
    : `RENDER ${actual}× of ${native}× native · no drops`;
  el.classList.toggle('reduced', !!q.reduced);

  // ---- SC38c THE LIVE VALUE ALONE IS A FALSE NEGATIVE ---------------------
  // SC36 painted this row on every open specifically so it could not go stale,
  // and that was still not enough. Restoring takes two 3s windows over 55fps,
  // Settings opens only from the title, and the title is cheap — so a device
  // that shed hard during a battle reads "Full · 2x" about six seconds later.
  // Asking a player to read the live tier can therefore only ever produce a
  // false negative. The sub-line carries the half that cannot self-heal.
  const sub = $('setQualitySub');
  if (sub) {
    if (!q.sheds) {
      sub.textContent = q.fps == null
        ? 'never dropped quality yet'
        : `never dropped quality · ${q.fps} fps now`;
      sub.classList.remove('warn');
    } else {
      const mins = Math.round((q.shedAgoMs || 0) / 60000);
      const when = mins < 1 ? 'just now' : `${mins} min ago`;
      const at = q.lastShedFps != null ? ` at ${q.lastShedFps} fps` : '';
      sub.textContent = `DROPPED QUALITY ${q.sheds}× this visit · last${at}, ${when}`;
      sub.classList.add('warn');
    }
  }
  // the numbers behind the words, for a screenshot that has to settle it
  el.title = `perfMode=${q.reduced} render dpr=${q.dpr} device dpr=${q.devDpr} ` +
    `viewport=${q.w}x${q.h} sheds=${q.sheds} restores=${q.restores} ` +
    `lastShedFps=${q.lastShedFps} fpsNow=${q.fps}`;
}

function initSettings() {
  $('titleSettings').addEventListener('click', () => {
    audio.unlock();
    $('settingsDrawer').classList.add('open');
    paintToggles();
    paintQuality();
  });
  $('settingsClose').addEventListener('click', () => $('settingsDrawer').classList.remove('open'));
  // How to play / Type chart from settings
  $('setGuide').addEventListener('click', () => {
    $('settingsDrawer').classList.remove('open');
    openGuide('play');
  });
  $('setTypes').addEventListener('click', () => {
    $('settingsDrawer').classList.remove('open');
    openGuide('types');
  });
  $('setWhatsApp')?.addEventListener('click', async () => {
    if (!ctx.createWhatsAppLinkCode) return;
    const button = $('setWhatsApp');
    button.disabled = true;
    button.textContent = 'Creating…';
    try {
      const result = await ctx.createWhatsAppLinkCode();
      const command = `/nomad link ${result.code}`;
      button.textContent = result.code;
      if (navigator.clipboard?.writeText) void navigator.clipboard.writeText(command).catch(() => {});
      ctx.notify?.(`${command} · send this in an enabled group within 10 minutes`, 12000);
    } catch (error) {
      button.textContent = 'Claim device';
      ctx.notify?.(error?.message || 'Could not create a WhatsApp link code.');
    } finally {
      setTimeout(() => paintToggles(), 12000);
    }
  });
  $('setSound').addEventListener('click', () => {
    audio.unlock();
    audio.setMuted(!audio.muted());
    paintToggles();
    if (ctx.paintMute) ctx.paintMute();
  });
  $('setNarrator').addEventListener('click', () => {
    audio.unlock();
    audio.setNarrator(!audio.narratorOn());
    paintToggles();
    if (audio.narratorOn()) audio.vo('title-call', { interrupt: true });
  });
  // P5: EXPLICIT SHAO — OFF filters battle commentary to clean lines only
  $('setShao').addEventListener('click', () => {
    audio.unlock();
    commentary.setExplicit(!commentary.explicitOn());
    paintToggles();
  });
  // SC3 (a): haptics toggle (no-op where unsupported; a test thump on enable)
  $('setHaptics').addEventListener('click', () => {
    haptics.setEnabled(!haptics.enabled());
    paintToggles();
    if (haptics.enabled()) haptics.buzz([30, 40, 30]);
  });
  // SC5 (WS2): intro cinematic toggle — next SESSION honors it (session-gated)
  $('setCine').addEventListener('click', () => {
    const on = localStorage.getItem('nomadmon-cine') !== '0';
    localStorage.setItem('nomadmon-cine', on ? '0' : '1');
    paintToggles();
  });
  // V2: Replay Intro — closes the drawer and plays the intro over the title
  $('setIntroReplay').addEventListener('click', () => {
    if (!ctx.replayIntro) return;
    $('settingsDrawer').classList.remove('open');
    ctx.replayIntro();
  });
  $('setInstall').addEventListener('click', async () => {
    if (!installPrompt) return;
    const p = installPrompt; installPrompt = null;
    $('installRow').style.display = 'none';
    try { await p.prompt(); } catch { /* dismissed */ }
  });
  // SC2-B: SWITCH PLAYER reopens the name picker (guest data stays intact)
  $('setPlayer').addEventListener('click', () => {
    $('settingsDrawer').classList.remove('open');
    if (ctx.openPlayerPicker) ctx.openPlayerPicker();
  });
}

// ---------------------------------------------------------------- init
export function initShell(c) {
  ctx = c;
  const deck = ctx.deck;
  ownedFn = ctx.owned || null;             // SC2-B ownership gate
  deckHintDefault = $('deckHint').textContent;
  loadDeck(deck);
  if (ctx.syncWhatsAppDeck) void ctx.syncWhatsAppDeck(deckSel);
  paintSlots(deck);
  initSettings();

  // SC2-A: tap a filled tray slot to remove that card (whoosh back)
  for (const el of document.querySelectorAll('.dslot')) {
    el.addEventListener('click', () => {
      const slot = el.dataset.slot;
      const isSup = slot.startsWith('s');
      const idx = Number(slot.slice(1));
      const pool = isSup ? deckSel.supports : deckSel.chars;
      const id = pool[idx];
      if (!id) return;
      pool.splice(idx, 1);
      audio.play('deckremove', { gain: 0.8 });
      saveDeck();
      paintSlots(deck);
    });
  }

  // title menu
  // SC7 WS-A: battle is EARNED — while the pack gate holds, both battle
  // buttons are sealed (main.js owns the presentation + Shao's mockery).
  $('menuSolo').addEventListener('click', () => {
    audio.unlock();
    if (ctx.battleLocked && ctx.battleLocked()) { ctx.lockedTap(); return; }
    audio.loadBattle();                         // entrance/first hit must be warm
    audio.play('msolo', { gain: 0.9 });
    go('deck', { after: () => paintSlots(deck) });
  });
  // SC33 BOSS GAUNTLET — locked until the collection is deep enough to survive
  // three battles without healing.
  $('menuGauntlet').addEventListener('click', () => {
    audio.unlock();
    if (ctx.battleLocked && ctx.battleLocked()) { ctx.lockedTap(); return; }
    if (!(ctx.gauntletUnlocked && ctx.gauntletUnlocked())) {
      audio.play('tick', { gain: 0.5 });
      return;
    }
    audio.loadBattle();
    audio.play('msolo', { gain: 0.9 });
    gauntletMode = true;
    go('deck', { after: () => paintSlots(deck) });
  });
  $('gpBtn').addEventListener('click', () => {
    audio.play('tick', { gain: 0.6 });
    const fn = gauntOnContinue; gauntOnContinue = null;
    if (fn) fn();
  });
  $('menuMp').addEventListener('click', () => {
    audio.unlock();
    if (ctx.battleLocked && ctx.battleLocked()) { ctx.lockedTap(); return; }
    audio.loadBattle();
    audio.play('mmp', { gain: 0.9 });
    if (ctx.openMultiplayer) ctx.openMultiplayer();
  });
  let packEntryBusy = false;
  $('menuPack').addEventListener('click', async () => {
    const unlocked = audio.unlock();
    // SC2-B: no credits -> the button shows the countdown and stays put
    if (ctx.canOpenPack && !ctx.canOpenPack()) return;
    if (packEntryBusy) return;
    packEntryBusy = true;
    try {
      // `mpack` is part of the transition, so its decoded buffer and the
      // resumed graph are prerequisites for entering the fan/pack route.
      // Previously the larger load started only inside main.js, after this
      // play() call, leaving the menu cue as the one cold-entry race.
      await Promise.all([audio.loadPackCore(), unlocked]);
    } catch (err) {
      packEntryBusy = false;
      console.error('[nomadmon-audio] pack entry sfx failed:', err);
      return;
    }
    audio.play('mpack', { gain: 0.9 });
    go('fan', { after: () => { packEntryBusy = false; ctx.openPack(); } });
  });
  $('menuCollection').addEventListener('click', () => {
    audio.unlock();
    audio.play('mcoll', { gain: 0.85 });
    // SC22: this button's sub-label promises "the full binder", so deliver it.
    // Previously it only reached the fan, and on phones (<=400px, where the
    // deck hotspot is hidden) the binder was unreachable by ANY route.
    // Closing the binder still drops you into the fan, so both views stay live.
    go('fan', { after: () => { if (ctx.openBinder) ctx.openBinder(); } });
  });
  // SC8: shared soft hover tick on pointer devices (never touch), rate-limited
  if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
    let lastTick = 0;
    for (const id of ['menuSolo', 'menuMp', 'menuPack', 'menuCollection']) {
      $(id).addEventListener('pointerenter', () => {
        const now = performance.now();
        if (now - lastTick < 90) return;
        lastTick = now;
        audio.play('mhover', { gain: 0.35 });
      });
    }
  }
  // SC3 (e): leaderboard entry (title footer). SC9-A: the lbBack pill is
  // dead — the crest home medallion (main.js) is the ONE home affordance.
  $('titleLb').addEventListener('click', () => { audio.unlock(); go('lb'); });

  // deck builder (SC9-A: deckBack pill dead — medallion routes via deckHome)
  $('deckQuick').addEventListener('click', () => {
    audio.play('shimmer', { gain: 0.5 });
    quickDeck(deck);
    paintSlots(deck);
  });
  $('deckClear').addEventListener('click', () => {
    deckSel = { chars: [], supports: [] };
    saveDeck();
    paintSlots(deck);
  });
  $('deckStart').addEventListener('click', () => {
    if (!deckLegal()) return;
    launchLuckyDraw({ chars: [...deckSel.chars], supports: [...deckSel.supports] });
  });
  // V2 FORGE: a finished squad (5 fighters + 2 supports) takes the same launch
  // path the LUCKY DRAW result takes; FATE with two picks runs the real reels.
  function launchResolved(resolved) {
    if (ctx.recordFielded) ctx.recordFielded(resolved.chars);
    if (mpDeckHandler) { mpDeckHandler(resolved); return; }
    if (gauntletMode) {
      gauntletMode = false;
      ctx.startGauntlet(resolved);       // owns its own go('battle')
      return;
    }
    go('battle', { after: () => ctx.startSolo(resolved, difficulty()) });
  }
  forgeLaunch = launchResolved;
  // THE FORGE: three picks, two reels, inside the star budget (picked.draw).
  forgeLucky = (picked) => launchLuckyDraw(picked);
  function launchLuckyDraw(picked) {
    audio.unlock();
    let seed;
    if (mpDeckHandler && mpDrawOpts) seed = deriveMultiplayerDrawSeed(mpDrawOpts.seed, mpDrawOpts.side);
    else {
      const words = new Uint32Array(1);
      try { crypto.getRandomValues(words); seed = words[0]; }
      catch { seed = (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0; }
    }
    let debugSeed = null;
    try {
      debugSeed = new URLSearchParams(location.search).get('drawseed');
      if (debugSeed && !debugSeed.toLowerCase().startsWith('type')) seed = debugSeed;
    } catch { /* no URL in harness */ }
    let draw;
    try {
      draw = resolveDraw({ cards: deck, owned: ownedFn || (() => true), picks: picked.chars,
        history: ctx.fieldHistory ? ctx.fieldHistory() : {}, seed, debugSeed, ...(picked.draw || {}) });
    } catch (err) {
      $('deckHint').textContent = err.message;
      audio.play('tick', { gain: 0.5, rate: 0.7 });
      return;
    }
    draw.supports = picked.supports;
    if (window.__nm) window.__nm.lastDraw = draw;
    else window.__nm = { lastDraw: draw };
    const resolved = { chars: [...draw.result], supports: [...picked.supports] };
    if (picked.finish) picked.finish(resolved);   // forge: holo claims + mastery for the full five
    const finish = () => launchResolved(resolved);
    go('draw', { after: () => ctx.startLuckyDraw(draw, finish) });
  }

  // SC3 (d): AI difficulty pills (persisted)
  // SC36: the row collapses to a chip that names the current setting (see the
  // note in index.html). The pills and their handlers are unchanged; the chip
  // is a disclosure control layered on top, and it also has to SAY what the
  // setting is, because a collapsed control that hides its own value is worse
  // than the row it replaced.
  // SC38d: the disclosure CHIP is gone with the rest of the meta row. The pills
  // and their persistence are untouched — they now live in #deckPanel — and the
  // current setting is surfaced on ENTER THE ARENA, which is the moment it
  // actually matters and the one control the player cannot leave without
  // pressing. A collapsed control that hides its own value is worse than the
  // row it replaced; this one does not hide it, it moves it to the commit.
  const paintDiff = () => {
    let label = '', xp = '';
    for (const b of document.querySelectorAll('#deckDiff .diff')) {
      const on = b.dataset.diff === difficulty();
      b.classList.toggle('on', on);
      if (on) {
        label = b.dataset.label || b.textContent.trim();
        xp = b.dataset.xp || '1.0\u00d7 XP';
      }
    }
    const vs = document.getElementById('deckVs');
    if (vs) vs.textContent = `vs ${label || 'Normal'} \u00b7 ${xp || '1.0\u00d7 XP'}`;
  };
  for (const b of document.querySelectorAll('#deckDiff .diff')) {
    b.addEventListener('click', () => {
      try { localStorage.setItem(DIFF_KEY, b.dataset.diff); } catch { /* private */ }
      audio.play('tick', { gain: 0.5 });
      paintDiff();
    });
  }
  paintDiff();

  // SC38d THE OPTIONS PANEL — one quiet gear, everything Jack asked off the band.
  {
    const panel = document.getElementById('deckPanel');
    const gear = document.getElementById('deckCustom');
    const shut = () => {
      if (!panel) return;
      panel.classList.remove('open');
      panel.setAttribute('aria-hidden', 'true');
      if (gear) gear.setAttribute('aria-expanded', 'false');
    };
    if (gear && panel) {
      gear.addEventListener('click', () => {
        panel.classList.add('open');
        panel.setAttribute('aria-hidden', 'false');
        gear.setAttribute('aria-expanded', 'true');
        audio.play('tick', { gain: 0.5 });
      });
      const done = document.getElementById('deckPanelClose');
      if (done) done.addEventListener('click', () => { audio.play('tick', { gain: 0.4 }); shut(); });
      // tapping the scrim closes it; tapping the box does not
      panel.addEventListener('click', (e) => { if (e.target === panel) shut(); });
    }
  }

  // interstitial actions
  $('interContinue').addEventListener('click', () => {
    const fn = interOnContinue; interOnContinue = null;
    (fn || (() => go('title')))();
  });
  $('interRematch').addEventListener('click', () => {
    const fn = interOnRematch;
    if (fn) fn();
  });
  $('interShare').addEventListener('click', () => {
    if (interOnShare) interOnShare();
  });

  // first-gesture: unlock audio, greet, start title theme. SC6: while the
  // ENTRY SCREEN is up it owns the first tap (entryEnter -> BANG + greet) —
  // a stray tap must not start the music before the player enters.
  addEventListener('pointerdown', () => {
    const entry = document.getElementById('entry');
    if (entry && entry.classList.contains('on')) return;
    if (document.getElementById('gate')?.hidden === false) return;   // the ENTER GATE owns it too
    audio.unlock();
    if (screen === 'title') greet();
  }, { capture: true });

  return {
    go, boot, showInterstitial, currentScreen, selectedDeck,
    // SC32 opponent scaling + rewards
    winStreak, setWinStreak, recordStreak, soloWins, recordSoloWin,
    showBattleReward, showBattleProgress,
    // SC33 boss gauntlet
    showGauntletLeg, showGauntletEnd, paintGauntletButton,
    // SC9-A: home-from-deck — an MP deck pick returns to the MP lobby (the
    // match must be cancellable there), everything else goes home to title.
    deckHome: () => {
      if (mpDeckHandler) { mpDeckHandler = null; go('mp'); return; }
      go('title');
    },
    // SC33: this wrapper took ONLY rng and silently dropped opts, so
    // `aiDeck(undefined, { difficulty })` on the solo path threw its options
    // away and every opponent was drafted at the NORMAL bias regardless of the
    // tier the player picked — scaling.js's DIFF_BIAS has never once been
    // applied in the app, though the sim has always modelled it. Forwarded now.
    aiDeck: (rng, opts) => aiDeck(deck, rng, opts),
    // SC2-A carousel deck API
    deckApi: {
      sel: () => deckSel,
      // v11: forging spends cards; drop any the saved squad no longer owns
      revalidate: () => { loadDeck(deck); saveDeck(); },
      toggle: (id) => toggleCard(deck, id),
      isSupport,
      legal: deckLegal,
      paint: () => paintSlots(deck),
      onChange: setOnDeckChange,
      // V2 FORGE hooks
      launch: (resolved) => forgeLaunch && forgeLaunch(resolved),
      lucky: (picked) => forgeLucky && forgeLucky(picked),
      home: () => {
        if (mpDeckHandler) { mpDeckHandler = null; go('mp'); return; }
        go('title');
      },
      difficulty,
      setDifficulty: (d) => {
        const b = document.querySelector(`#deckDiff .diff[data-diff="${d}"]`);
        if (b) b.click();
      },
      gauntlet: () => gauntletMode,
      mp: () => !!mpDeckHandler,
      // SC2-B: re-filter the stored selection after a player switch
      reload: () => {
        loadDeck(deck); paintSlots(deck);
        if (ctx.syncWhatsAppDeck) void ctx.syncWhatsAppDeck(deckSel);
      },
    },
  };
}

// boot(name) — first paint, no veil
export function boot(name) {
  screen = name;
  showScreenEl(name);
  setBodyMode();
  if (name === 'title') ctx.showTitle3D();
  if (name === 'deck' && ctx.showDeck3D) ctx.showDeck3D();
  if (name === 'fan' && ctx.prepFan) ctx.prepFan();   // SC9-B
}
